import { useCallback } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { AiPromptBox } from "@zapier/enriched-prompter";
import {
  ContentfulTemplate,
  emitUserInteractionEventSetter,
  isViewOnlyDowntimeOverrideAtom,
} from "@zapier/canvas-state";
import { CreateCanvasThumbnail } from "../../../components/CreateCanvasThumbnail";
import { TemplateThumbnails } from "../../../components/TemplateThumbnails";
import { Guides } from "../../../components/Guides";
import styles from "./ZeroState.module.css";

export function ZeroState() {
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);
  const isViewOnlyDowntime = useAtomValue(isViewOnlyDowntimeOverrideAtom);

  const handleTemplateClick = useCallback(
    async (template: ContentfulTemplate) => {
      const slug = template.fields.slug;

      window.open(`/app/canvas/template/${slug}`, "_blank");

      await emitUserInteractionEvent({
        eventAction: "click",
        interactionGoal: "Preview template on list page",
        rawText: slug,
      });
    },
    [emitUserInteractionEvent],
  );

  return (
    <section className={styles.section}>
      <div className={styles.zeroStateContainer}>
        <div className={styles.leftColumn}>
          <Text color="TextDefault">
            {
              "Visualize your team's most important processes and automate more of your business through AI-powered systems."
            }
          </Text>
          <div className={styles.icons}>
            <div className={styles.iconRow}>
              <Icon name="miscErrorHandler" size={20} color="TextLink" />
              <Text color="TextDefault" type="SmallPrint2">
                Diagramming tools
              </Text>
            </div>
            <div className={styles.iconRow}>
              <Icon name="miscEvergreenAI" size={20} color="TextLink" />
              <Text color="TextDefault" type="SmallPrint2">
                Smart recommendations
              </Text>
            </div>
            <div className={styles.iconRow}>
              <Icon name="navConnectedAssets" size={20} color="TextLink" />
              <Text color="TextDefault" type="SmallPrint2">
                Build entire systems
              </Text>
            </div>
          </div>
          <AiPromptBox
            emitUserInteractionEvent={emitUserInteractionEvent}
            handleSubmit={(prompt) =>
              (window.location.href = `/app/canvas/ai?prompt=${encodeURIComponent(prompt)}`)
            }
          />
        </div>
        <div className={styles.rightColumn}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            className={styles.video}
            src="https://res.cloudinary.com/zapier-media/video/upload/q_auto,f_auto/v1736805161/Videos/Canvas_in_action_-_2_hiwpyt.mp4"
            autoPlay
            loop
          />
        </div>
      </div>
      <div className={styles.thumbnailsWrapper}>
        <div className={styles.thumbnailsLeftColumn}>
          <div className={styles.sectionHeaderWrapper}>
            <Text type="SectionHeader" color="TextDefault">
              Start from scratch
            </Text>
          </div>
          <div className={styles.thumbnailWrapper}>
            <CreateCanvasThumbnail />
            <div className={styles.divider} />
          </div>
        </div>
        <div className={styles.thumbnailsRightColumn}>
          <div className={styles.sectionHeaderWrapper}>
            <Text type="SectionHeader" color="TextDefault">
              Explore starter systems
            </Text>
          </div>
          <TemplateThumbnails
            handleTemplateClick={handleTemplateClick}
            isDisabled={isViewOnlyDowntime}
          />
        </div>
      </div>
      <Guides />
    </section>
  );
}
